import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CustomOrderDetail } from "@/components/CustomOrderDetail";

const BADGE = {
  pending: "bg-amber-100 text-amber-700", quoted: "bg-violet-100 text-violet-700",
  changes_requested: "bg-orange-100 text-orange-700", rejected: "bg-rose-100 text-rose-700",
  confirmed: "bg-emerald-100 text-emerald-700",
};

export default function CustomerCustomRequests() {
  const [rows, setRows] = useState([]);
  const [paying, setPaying] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = async () => { const r = await api.get("/marketplace/custom/my-requests").catch(() => ({ data: { requests: [] } })); const rr = r.data.requests || []; setRows(rr); return rr; };
  useEffect(() => { load(); }, []);
  const prevUnread = useRef(null);
  useEffect(() => {
    const tick = async () => {
      const rr = await load();
      const total = rr.reduce((a, x) => a + (x.unread || 0), 0);
      if (prevUnread.current !== null && total > prevUnread.current) toast.info("New message on a custom order");
      prevUnread.current = total;
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRzp = () => new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => res(false); document.body.appendChild(s);
  });

  const pay = async (r) => {
    setPaying(r.request_id);
    try {
      const { data } = await api.post(`/marketplace/custom/${r.request_id}/pay`, {});
      if (!(await loadRzp())) { toast.error("Could not load payment gateway"); setPaying(null); return; }
      const rzp = new window.Razorpay({
        key: data.key_id, order_id: data.order_id, amount: data.amount, currency: data.currency,
        name: "Suffle — Custom Order", description: `${r.number} · ₹${data.quote}`,
        prefill: { name: data.customer_name || "", email: data.customer_email || "" }, theme: { color: "#7c3aed" },
        handler: async (resp) => {
          try { await api.post(`/marketplace/custom/${r.request_id}/verify`, resp); toast.success("Payment verified — your custom order is confirmed!"); load(); }
          catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Verification failed"); }
        },
        modal: { ondismiss: () => setPaying(null) },
      });
      rzp.on("payment.failed", (x) => { toast.error("Payment failed", { description: x?.error?.description }); setPaying(null); });
      rzp.open();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setPaying(null); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6" data-testid="customer-custom-requests">
      <h1 className="mb-4 text-xl font-bold text-slate-900">My Custom Orders</h1>
      {rows.length === 0 ? <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400" data-testid="cco-empty">No custom orders yet. Find a customizable product and tap "Request customization".</p> : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.request_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`cco-row-${r.request_id}`}>
              <div className="flex items-center justify-between">
                <div><b className="text-sm">{r.number}</b> <span className="text-xs text-slate-400">· {r.product_name}</span></div>
                <div className="flex items-center gap-2">
                  {r.unread > 0 && <span data-testid={`cco-unread-${r.request_id}`} className="rounded-full bg-rose-500 px-2 py-0.5 text-xs font-bold text-white">{r.unread} new</span>}
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${BADGE[r.status] || "bg-slate-100"}`} data-testid={`cco-status-${r.request_id}`}>{r.status.replace(/_/g, " ")}</span>
                </div>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-xs text-slate-600">{r.requirements}</p>
              {r.quote_note && <p className="mt-1 text-xs italic text-slate-400">Seller: {r.quote_note}</p>}
              {r.status === "quoted" && (
                <div className="mt-3 flex items-center justify-between rounded-lg bg-violet-50 p-2">
                  <span className="text-sm font-semibold text-violet-800">Quote: ₹{r.quote_amount}</span>
                  <button onClick={() => pay(r)} disabled={paying === r.request_id} data-testid={`cco-pay-${r.request_id}`} className="rounded-full brand-gradient-bg px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{paying === r.request_id ? "…" : `Accept & Pay ₹${r.quote_amount}`}</button>
                </div>
              )}
              {r.status === "confirmed" && <p className="mt-2 text-xs font-semibold text-emerald-600" data-testid={`cco-confirmed-${r.request_id}`}>Paid ₹{r.quote_amount} · Confirmed ✓</p>}
              <button onClick={() => setDetail(r)} data-testid={`cco-detail-${r.request_id}`} className="mt-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">View details &amp; chat{r.messages?.length ? ` (${r.messages.length})` : ""}</button>
            </div>
          ))}
        </div>
      )}
      {detail && (
        <CustomOrderDetail request={detail} role="customer" onClose={() => setDetail(null)}
          onReload={async () => { const rr = await load(); setDetail((d) => d ? (rr.find((x) => x.request_id === d.request_id) || d) : d); }} />
      )}
    </div>
  );
}
